import Grid from "@mui/material/Grid"
import { Box } from "@mui/system"
import React, { useEffect, useState, useRef } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { APP_CONFIG } from "src/app_config"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { useNavigate, useParams } from "react-router-dom"
import { useRecoilState } from "recoil"
import GoogleIcon from "src/assets/icons/google.svg"
import LineIcon from "src/assets/icons/line.png"
import OtherSignupBack from "src/assets/imgs/other_signup_back.png"
import UserSignupBack from "src/assets/imgs/user_signup_back.png"
import InputTextField from "src/components/atom/InputTextField"
import { useLoginContext } from "src/hooks/useLoginContext"
import { MEMBER_TYPE, RESPONSE_CODE, SNACKBAR_TYPES, STORAGE_KEY } from "src/modules/const"
import { getUserType } from "src/modules/helper"
import { validateField } from "src/modules/validation"
import { AuthenticationService } from "src/openapi"
import { ShowProgress } from "src/states/showProgress"
import { Button, Container, Image, Typography } from "src/UILibrary"
import * as yup from "yup"
import { ShowHeader } from "../../states/showHeader"
import { ShowToast } from "../../states/showToast"
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth"
import { Amplify, Auth } from "aws-amplify"
import { AWS_CONFIG } from "src/aws_config"

Amplify.configure(AWS_CONFIG)

export const Signup = () => {
  const { t } = useTranslation("translation", { keyPrefix: "signup" })

  const isPC = useMediaQuery({ query: "(min-width: 1020px)" })

  const navigate = useNavigate()

  const { refreshMasterData } = useLoginContext()

  const { type } = useParams()
  const userType = getUserType(type)

  if (!userType) {
    navigate("/signup/type")
  }

  const [, setShowHeader] = useRecoilState(ShowHeader)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [verified, setVerified] = useState<boolean>(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")

  const [emailValidationMessage, setEmailValidationMessage] = useState<string>("")
  const [passwordValidationMessage, setPasswordValidationMessage] = useState<string>("")

  useEffect(() => {
    setShowHeader(false)
  })

  const validate = async () => {
    let ret = verified

    if (!verified) {
      setShowToast({ show: true, text: t("please_verify_recaptcha"), type: SNACKBAR_TYPES.WARNING })
    }

    try {
      await validateField(
        yup.object().shape({
          email: yup
            .string()
            .required(t("please_input_email"))
            .email(t("please_input_correct_email")),
        }),
        "email",
        email
      )
      ret = ret && true
      setEmailValidationMessage("")
    } catch (err: any) {
      setEmailValidationMessage(err.message)
      ret = ret && false
    }

    try {
      await validateField(
        yup.object().shape({
          password: yup
            .string()
            .required(t("please_input_password"))
            .min(8, t("password_length_min"))
            .max(20, t("password_length_max"))
            .test("isValidPassword", t("password_regex"), (value) => {
              const hasLowerCase = /[a-z]/.test(value)
              const hasNumber = /[0-9]/.test(value)

              return hasLowerCase && hasNumber
            }),
        }),
        "password",
        password
      )
      ret = ret && true
      setPasswordValidationMessage("")
    } catch (err: any) {
      setPasswordValidationMessage(err.message)
      ret = ret && false
    }

    return ret
  }

  const handleSignup = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)

    refreshMasterData()

    AuthenticationService.signUp({
      email: email,
      password: password,
      userType: userType as number,
    })
      .then((res) => {
        setShowProgress(false)
        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          navigate("/signup/verification", {
            state: { email: email, userId: res.data?.UserSub, type: userType },
          })
        } else if (res.status === RESPONSE_CODE.CONFLICT) {
          setShowToast({ show: true, text: t("already_registered"), type: SNACKBAR_TYPES.WARNING })
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleGoogleSignin = () => {
    localStorage.setItem(STORAGE_KEY.TEMP_USER_TYPE, userType?.toString() as string)
    Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google })
  }

  const handleLineSignin = () => {
    localStorage.setItem(STORAGE_KEY.TEMP_USER_TYPE, userType?.toString() as string)
    Auth.federatedSignIn({ customProvider: "LINE-Login" })
  }

  return (
    <Grid id="whole-app" style={{ position: "relative", height: "100vh" }}>
      {isPC ? (
        <Grid
          id="main"
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "inherit" }}
        >
          <Container sx={{ width: "lg", display: "flex" }}>
            <Box sx={{ width: "50%", position: "relative" }}>
              <Image
                src={userType === MEMBER_TYPE.USER ? UserSignupBack : OtherSignupBack}
                sx={{ width: "100%", zIndex: 1 }}
              />
              <Typography.SignupLogo
                sx={{
                  position: "absolute",
                  zIndex: 2,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                TASU
              </Typography.SignupLogo>
            </Box>
            <Box sx={{ width: "50%", height: "auto", display: "flex" }}>
              <Box sx={{ display: "flex", mx: "auto", my: "auto", flexDirection: "column" }}>
                <InputTextField
                  name={t("email")}
                  required
                  placeholder={t("email_placeholder")}
                  text={email}
                  handleText={setEmail}
                  validationMessage={emailValidationMessage}
                  maxLength={50}
                />
                <InputTextField
                  name={t("password")}
                  required
                  placeholder={t("password_placeholder")}
                  sx={{ mt: "20px" }}
                  password
                  text={password}
                  handleText={setPassword}
                  validationMessage={passwordValidationMessage}
                />
                <ReCAPTCHA
                  style={{ display: "block", marginTop: "20px" }}
                  sitekey={APP_CONFIG.GOOGLE_RECAPTCHA_SITE_KEY}
                  hl="ja"
                  ref={recaptchaRef}
                  onChange={() => setVerified(true)}
                  onExpired={() => setVerified(false)}
                />
                <Typography.DetailText sx={{ mt: "20px", width: "375px" }}>
                  {t("agree_term_policy")}
                </Typography.DetailText>
                <Button
                  variant="outlined"
                  sx={{
                    mt: "40px",
                    width: "240px",
                    borderRadius: 0,
                    borderColor: "text.primary",
                    py: "16px",
                  }}
                  onClick={() => handleSignup()}
                >
                  <Typography.ButtonText
                    sx={{ fontSize: "16px", fontFamily: "Noto Sans JP !important" }}
                  >
                    {t("register_member")}
                  </Typography.ButtonText>
                </Button>

                <Box
                  sx={{
                    mt: "20px",
                    width: "240px",
                    p: 0,
                    cursor: "pointer",
                    display: "flex",
                  }}
                  onClick={() => navigate("/signin")}
                >
                  <Typography.ExtraSmallText sx={{ mr: "10px" }}>
                    {t("here_to_login")}
                  </Typography.ExtraSmallText>
                  <Typography.ExtraSmallText>{">"}</Typography.ExtraSmallText>
                </Box>
                <Typography.SubHeadNormal sx={{ mt: "80px" }}>
                  {t("signup_with_other_service")}
                </Typography.SubHeadNormal>
                <Box
                  sx={{
                    mt: "20px",
                    display: "flex",
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "white",
                      width: "230px",
                      mr: "20px",
                      my: "auto",
                      p: "10px",
                      ":hover": {
                        bgcolor: "#2DA389",
                      },
                    }}
                    onClick={() => handleGoogleSignin()}
                  >
                    <Image src={GoogleIcon} sx={{ height: "25px" }} />
                    <Typography.ButtonText
                      sx={{
                        fontFamily: "Noto Sans JP !important",
                        ml: "20px",
                        my: "auto",
                        fontWeight: "500",
                      }}
                    >
                      {t("google_signup")}
                    </Typography.ButtonText>
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#06C755",
                      width: "230px",
                      my: "auto",
                      p: "10px",
                      ":hover": {
                        bgcolor: "#2DA389",
                      },
                    }}
                    onClick={() => handleLineSignin()}
                  >
                    <Image src={LineIcon} sx={{ width: "25px", height: "25px" }} />
                    <Typography.ButtonText
                      sx={{
                        fontFamily: "Noto Sans JP !important",
                        ml: "20px",
                        my: "auto",
                        fontWeight: "500",
                        color: "white",
                      }}
                    >
                      {t("line_signup")}
                    </Typography.ButtonText>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Container>
        </Grid>
      ) : (
        <Container
          sx={{
            width: "100%",
            display: "block",
            p: "0px !important",
            backgroundImage: `url(${
              userType === MEMBER_TYPE.USER ? UserSignupBack : OtherSignupBack
            })`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box width="100%" height="50px"></Box>
          <Typography.SignupLogo sx={{ textAlign: "center" }}>TASU</Typography.SignupLogo>
          <Box
            sx={{
              display: "block",
              width: "calc(100% - 20px)",
              mx: "10px",
              mt: "50px",
              bgcolor: "white",
              px: "10px",
              py: "20px",
            }}
          >
            <Box sx={{ display: "flex", mx: "auto", my: "auto", flexDirection: "column" }}>
              <InputTextField
                name={t("email")}
                required
                placeholder={t("email_placeholder")}
                text={email}
                handleText={setEmail}
                validationMessage={emailValidationMessage}
                maxLength={50}
              />
              <InputTextField
                name={t("password")}
                required
                placeholder={t("password_placeholder")}
                sx={{ mt: "20px" }}
                password
                text={password}
                handleText={setPassword}
                validationMessage={passwordValidationMessage}
              />
              <ReCAPTCHA
                style={{ display: "block", marginTop: "20px", maxWidth: "calc(100% - 20px)" }}
                sitekey={APP_CONFIG.GOOGLE_RECAPTCHA_SITE_KEY}
                hl="ja"
                ref={recaptchaRef}
                onChange={() => setVerified(true)}
                onExpired={() => setVerified(false)}
              />
              <Typography.DetailText sx={{ mt: "20px", width: "100%" }}>
                {t("agree_term_policy")}
              </Typography.DetailText>
              <Button
                variant="outlined"
                sx={{
                  mt: "40px",
                  width: "240px",
                  borderRadius: 0,
                  borderColor: "text.primary",
                  py: "16px",
                }}
                onClick={() => handleSignup()}
              >
                <Typography.ButtonText
                  sx={{ fontSize: "16px", fontFamily: "Noto Sans JP !important" }}
                >
                  {t("register_member")}
                </Typography.ButtonText>
              </Button>

              <Box
                sx={{
                  mt: "20px",
                  width: "240px",
                  p: 0,
                  cursor: "pointer",
                  display: "flex",
                }}
                onClick={() => navigate("/signin")}
              >
                <Typography.ExtraSmallText sx={{ mr: "10px" }}>
                  {t("here_to_login")}
                </Typography.ExtraSmallText>
                <Typography.ExtraSmallText>{">"}</Typography.ExtraSmallText>
              </Box>
              <Typography.SubHeadNormal sx={{ mt: "80px", fontSize: "18px" }}>
                {t("signup_with_other_service")}
              </Typography.SubHeadNormal>
              <Box
                sx={{
                  mt: "20px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "white",
                    width: "230px",
                    mr: "20px",
                    my: "auto",
                    p: "10px",
                    ":hover": {
                      bgcolor: "#2DA389",
                    },
                  }}
                  onClick={() => handleGoogleSignin()}
                >
                  <Image src={GoogleIcon} sx={{ height: "25px" }} />
                  <Typography.ButtonText
                    sx={{
                      fontFamily: "Noto Sans JP !important",
                      ml: "20px",
                      my: "auto",
                      fontWeight: "500",
                    }}
                  >
                    {t("google_signup")}
                  </Typography.ButtonText>
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#06C755",
                    width: "230px",
                    my: "auto",
                    p: "10px",
                    mt: "20px",
                    ":hover": {
                      bgcolor: "#2DA389",
                    },
                  }}
                  onClick={() => handleLineSignin()}
                >
                  <Image src={LineIcon} sx={{ width: "25px", height: "25px" }} />
                  <Typography.ButtonText
                    sx={{
                      fontFamily: "Noto Sans JP !important",
                      ml: "20px",
                      my: "auto",
                      fontWeight: "500",
                      color: "white",
                    }}
                  >
                    {t("line_signup")}
                  </Typography.ButtonText>
                </Button>
              </Box>
            </Box>
          </Box>
          <Box width="100%" height="50px"></Box>
        </Container>
      )}
    </Grid>
  )
}
